/** Passenger categories continue to describe hotel occupancy, independently of age. */
export type ChildCategory = "cwb" | "cnb" | "infant";
export type ChildPassenger = { id: string; category: ChildCategory | "unassigned"; age: number | null };
export type ChildAgeBand = { id: string; minAge: number; maxAge: number };
export type ChildCost = ChildPassenger & { label: string; amount: number };
export type ChildCounts = { adults?: number | string | null; cwb?: number | string | null; cnb?: number | string | null; infant?: number | string | null; childPassengers?: ChildPassenger[] };
export type AgeRateRow = { id: string; label: string; kind: "adult" | "child" | "infant" | "band"; minAge?: number; maxAge?: number; rate: number; quantity: number; childIds: string[] };
export type ActivityAgePricing = { version: 1; rows: AgeRateRow[]; unitMultiplier: number; passengerSnapshot: string };
export type AgePricedActivity = {
  attendance?: { adults: number; children: number; infants: number };
  category?: string; quantity?: number; quantityMode?: string; transferMode?: string;
  vehicleCost?: number; vehicleCount?: number; costForAdult?: number; costForCWB?: number; costForInfant?: number;
  childAgeBands?: ChildAgeBand[]; agePricing?: ActivityAgePricing;
};
export type AgeTariff = { costForAdult?: number; costForCWB?: number; costForInfant?: number; childAgeRates?: Record<string, number | null> };
const categories: ChildCategory[] = ["cwb", "cnb", "infant"];
const number = (value: unknown): number => Number.isFinite(Number(value)) ? Number(value) : 0;
const count = (value: unknown): number => Math.max(0, Math.floor(number(value)));
const groupCategories = new Set(["Transfers", "Private Tours", "Private Transfers"]);

/** Normalizes optional ages and seeds missing category rows without deleting surplus records. */
export const normalizeChildPassengers = (value: unknown, counts: ChildCounts): ChildPassenger[] => {
  const used = new Set<string>();
  const passengers: ChildPassenger[] = [];
  for (const raw of Array.isArray(value) ? value : []) {
    if (!raw || (!categories.includes(raw.category) && raw.category != null && raw.category !== "unassigned" && raw.category !== "")) continue;
    const category = categories.includes(raw.category) ? raw.category as ChildCategory : "unassigned";
    let id = typeof raw.id === "string" && raw.id.trim() ? raw.id.trim() : `child-${category}-${passengers.length + 1}`;
    while (used.has(id)) id += "-next";
    used.add(id);
    const age = raw.age === null || raw.age === undefined || raw.age === "" ? null : Number(raw.age);
    passengers.push({ id, category, age: age !== null && Number.isInteger(age) && age >= 0 ? age : null });
  }
  for (const category of categories) {
    let present = passengers.filter((child) => child.category === category).length;
    while (present < count(counts[category])) {
      let id = `child-${category}-${++present}`;
      while (used.has(id)) id += "-next";
      used.add(id);
      passengers.push({ id, category, age: null });
    }
  }
  return passengers;
};

/** Returns the stable category roster covered by the current passenger counts. */
export const activeChildPassengers = (counts: ChildCounts): Array<ChildPassenger & { category: ChildCategory }> => {
  const passengers = normalizeChildPassengers(counts.childPassengers, counts);
  const seen = { cwb: 0, cnb: 0, infant: 0 };
  return passengers.flatMap((child) => {
    if (child.category === "unassigned" || seen[child.category]++ >= count(counts[child.category])) return [];
    return [{ ...child, category: child.category as ChildCategory }];
  });
};

/** Captures age/count inputs so later changes can be reviewed instead of silently repriced. */
export const childPricingFingerprint = (counts: ChildCounts): string => JSON.stringify({ adults: count(counts.adults), children: activeChildPassengers(counts) });

/** Supplies readable, consistent child labels for every selling-price surface. */
export const childCostLabel = (child: ChildPassenger): string => {
  const category = child.category === "cwb" ? "CWB" : child.category === "cnb" ? "CNB" : child.category === "infant" ? "Infant" : "Child";
  return child.age == null ? category : `${category} (${child.age} yrs)`;
};

/** Displays standard activity rates consistently, including older saved labels. */
export const activityAgeRateLabel = (row: { id: string; label?: string }): string =>
  row.id === "child" ? "Children" : row.id === "infant" ? "Infants" : row.label || row.id;

/** Builds editable age rates, preserving zero prices and legacy service multipliers. */
export const buildActivityAgePricing = (activity: AgePricedActivity, counts: ChildCounts, tariff: AgeTariff = activity, regular: AgeTariff = tariff): ActivityAgePricing | undefined => {
  if (groupCategories.has(activity.category || "") || !(activity.childAgeBands || []).length) return undefined;
  const bands = activity.childAgeBands!.filter((band) => Number.isInteger(band.minAge) && Number.isInteger(band.maxAge) && band.minAge >= 0 && band.maxAge >= band.minAge);
  const rows: AgeRateRow[] = [
    { id: "adult", label: "Adults", kind: "adult", rate: number(tariff.costForAdult), quantity: 0, childIds: [] },
    ...bands.filter((band) => (tariff.childAgeRates?.[band.id] ?? regular.childAgeRates?.[band.id]) != null).map((band): AgeRateRow => ({ ...band, id: `band:${band.id}`, label: `Age ${band.minAge}–${band.maxAge}`, kind: "band", rate: number(tariff.childAgeRates?.[band.id] ?? regular.childAgeRates?.[band.id]), quantity: 0, childIds: [] })),
    { id: "child", label: "Children", kind: "child", rate: number(tariff.costForCWB), quantity: 0, childIds: [] },
    { id: "infant", label: "Infants", kind: "infant", rate: number(tariff.costForInfant), quantity: 0, childIds: [] },
  ];
  const all = count(counts.adults) + activeChildPassengers(counts).length;
  let remaining = activity.quantityMode === "pax" ? Math.min(all, Math.max(1, count(activity.quantity) || all)) : all;
  rows[0].quantity = activity.attendance && activity.agePricing?.version !== 1 ? count(activity.attendance.adults) : Math.min(count(counts.adults), remaining);
  remaining -= rows[0].quantity;
  const children = activeChildPassengers(counts);
  const participationOrder = [...children.filter((child) => child.category !== "infant"), ...children.filter((child) => child.category === "infant")];
  const selectedChildren = activity.attendance && activity.agePricing?.version !== 1
    ? [...children.filter((child) => child.category !== "infant").slice(0, count(activity.attendance.children)), ...children.filter((child) => child.category === "infant").slice(0, count(activity.attendance.infants))]
    : participationOrder.slice(0, remaining);
  for (const child of selectedChildren) {
    const band = child.age === null ? undefined : bands.find((entry) => child.age! >= entry.minAge && child.age! <= entry.maxAge);
    const hasRate = band && (tariff.childAgeRates?.[band.id] ?? regular.childAgeRates?.[band.id]) != null;
    const id = hasRate ? `band:${band!.id}` : child.category === "infant" ? "infant" : "child";
    const row = rows.find((entry) => entry.id === id)!;
    row.quantity++;
    row.childIds.push(child.id);
  }
  return { version: 1, rows, unitMultiplier: activity.quantityMode === "pax" ? 1 : Math.max(1, number(activity.quantity) || 1), passengerSnapshot: childPricingFingerprint(counts) };
};

/** Reassigns edited quantities deterministically, retaining compatible manual child selections first. */
export const assignAgeRateRows = (rows: AgeRateRow[], counts: ChildCounts): AgeRateRow[] => {
  const children = activeChildPassengers(counts);
  const used = new Set<string>();
  const result = rows.map((row) => ({ ...row, quantity: count(row.quantity), childIds: [] as string[] }));
  // Keep explicit assignments before allocating any otherwise-unassigned child.
  result.forEach((row, index) => {
    if (row.kind === "adult") return;
    for (const id of rows[index].childIds || []) {
      if (!used.has(id) && children.some((child) => child.id === id) && row.childIds.length < row.quantity) {
        row.childIds.push(id); used.add(id);
      }
    }
  });
  for (const row of result) {
    if (row.kind === "adult") continue;
    const fits = (child: ChildPassenger) => row.kind === "band" ? child.age !== null && child.age >= number(row.minAge) && child.age <= number(row.maxAge) : row.kind === "infant" ? child.category === "infant" : child.category !== "infant";
    for (const child of [...children.filter(fits), ...children.filter((child) => !fits(child))]) {
      if (row.childIds.length >= row.quantity) break;
      if (!used.has(child.id)) { row.childIds.push(child.id); used.add(child.id); }
    }
  }
  return result;
};

/** Computes exact row totals and individual allocations without enforcing business quantity limits. */
export const splitAgeActivityCost = (activity: AgePricedActivity, counts: ChildCounts) => {
  const children = activeChildPassengers(counts);
  const childTotals: Record<string, number> = Object.fromEntries(children.map((child) => [child.id, 0]));
  const totals = { adult: 0, cwb: 0, cnb: 0, infant: 0, shared: 0, childTotals, unmatched: 0 };
  const multiplier = activity.quantityMode === "pax" ? 1 : Math.max(1, number(activity.quantity ?? activity.agePricing?.unitMultiplier) || 1);
  const participating = new Set((activity.agePricing?.rows || []).filter((row) => row.kind !== "adult").flatMap((row) => [...new Set(row.childIds || [])].filter((id) => children.some((child) => child.id === id)).slice(0, count(row.quantity))));
  const allocationChildren = participating.size ? children.filter((child) => participating.has(child.id)) : children;
  for (const row of activity.agePricing?.rows || []) {
    const quantity = count(row.quantity);
    const rate = number(row.rate) * multiplier;
    if (row.kind === "adult") { totals.adult += rate * quantity; continue; }
    const ids = [...new Set(row.childIds || [])].filter((id) => children.some((child) => child.id === id)).slice(0, quantity);
    ids.forEach((id) => { childTotals[id] += rate; });
    const unmatched = (quantity - ids.length) * rate;
    totals.unmatched += unmatched;
    if (allocationChildren.length) allocationChildren.forEach((child) => { childTotals[child.id] += unmatched / allocationChildren.length; });
    else totals.adult += unmatched;
  }
  children.forEach((child) => { totals[child.category] += childTotals[child.id]; });
  if (activity.transferMode === "pvt") totals.shared = number(activity.vehicleCost) * Math.max(1, count(activity.vehicleCount) || 1) * multiplier;
  return totals;
};

/** Explains non-blocking review issues while keeping manual rate choices authoritative. */
export const agePricingWarnings = (activity: AgePricedActivity, counts: ChildCounts, tariff?: AgeTariff, regular?: AgeTariff): string[] => {
  const warnings: string[] = [];
  const bands = activity.childAgeBands || [];
  if (bands.some((band) => !Number.isInteger(band.minAge) || !Number.isInteger(band.maxAge) || band.minAge < 0 || band.maxAge < band.minAge)) warnings.push("Some age bands are invalid and cannot be used for autofill.");
  if (bands.some((band, index) => bands.slice(0, index).some((other) => band.minAge <= other.maxAge && band.maxAge >= other.minAge))) warnings.push("Age bands overlap; autofill uses the first matching band.");
  if (tariff && bands.some((band) => (tariff.childAgeRates?.[band.id] ?? regular?.childAgeRates?.[band.id]) == null)) warnings.push("Some age-band prices are missing; matching children use the standard children or infant rate for their category.");
  if (normalizeChildPassengers(counts.childPassengers, counts).length > activeChildPassengers(counts).length) warnings.push("Surplus child records are retained but excluded from autofill. Review passenger counts.");
  if (activity.agePricing) {
    if (activity.agePricing.passengerSnapshot !== childPricingFingerprint(counts)) warnings.push("Passenger counts or ages changed. Review assignments or reapply age pricing.");
    const rows = activity.agePricing.rows;
    if (rows.reduce((sum, row) => sum + count(row.quantity), 0) !== count(counts.adults) + activeChildPassengers(counts).length) warnings.push("Rate quantities differ from the trip passenger count. Review participation.");
    if (rows.some((row) => row.kind !== "adult" && count(row.quantity) !== (row.childIds || []).length)) warnings.push("Unmatched child charges are shared evenly. Review individual child costs.");
    const ids = rows.flatMap((row) => row.childIds || []);
    if (new Set(ids).size !== ids.length) warnings.push("A child is assigned to more than one rate row. Review activity participation.");
  }
  return warnings;
};

/** Replaces averaged age-activity charges with each child's actual charges, keeping category markup and shares. */
export const calculateChildSellingCosts = (input: ChildCounts & {
  days?: unknown; overrideTotalCostEnabled?: boolean | null;
  costPerCWB?: number | null; costPerCNB?: number | null; costPerInfant?: number | null;
}): ChildCost[] => {
  if (!input.childPassengers?.length) return [];
  const children = activeChildPassengers(input);
  const categoryPrices = { cwb: number(input.costPerCWB), cnb: number(input.costPerCNB), infant: number(input.costPerInfant) };
  const costs = children.map((child) => ({ ...child, label: childCostLabel(child), amount: categoryPrices[child.category] }));
  if (input.overrideTotalCostEnabled) return costs;
  for (const day of Array.isArray(input.days) ? input.days : []) {
    for (const activity of Array.isArray(day.activities) ? day.activities : []) {
      if (activity.agePricing?.version !== 1) continue;
      const split = splitAgeActivityCost(activity, input);
      costs.forEach((child) => { child.amount += (split.childTotals[child.id] || 0) - split[child.category] / Math.max(1, count(input[child.category])); });
    }
  }
  return costs;
};

/** Converts child selling amounts and refreshes labels without exposing internal activity prices. */
export const convertChildCosts = (costs: ChildCost[], factor = 1, precision = 0): ChildCost[] => {
  const scale = 10 ** precision;
  return costs.map((child) => ({ ...child, label: childCostLabel(child), amount: Math.round(child.amount * factor * scale) / scale }));
};

/** Updates repeated service units without changing saved rates or child assignments. */
export function normalizeAgePricingMultipliers(value: unknown): unknown {
  if (!Array.isArray(value)) return value;
  return value.map((day) => !day || typeof day !== "object" ? day : ({ ...day, activities: Array.isArray(day.activities) ? day.activities.map((activity: AgePricedActivity) => !activity || typeof activity !== "object" ? activity : ({ ...activity,
    ...(activity.agePricing?.version === 1 ? { agePricing: { ...activity.agePricing, unitMultiplier: activity.quantityMode === "pax" ? 1 : Math.max(1, number(activity.quantity ?? activity.agePricing.unitMultiplier) || 1) } } : {}),
  })) : day.activities }));
}
